import * as React from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { PASSWORD_MIN_LENGTH } from "@mandrac/shared";
import { Button, ButtonLink, ErrorText } from "@mandrac/ui";
import { PasswordField } from "../../components/fields";
import { api, ApiError } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { AuthShell } from "./AuthShell";

export function ResetPage() {
  const { t, te } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [password, setPassword] = React.useState("");
  const [repeat, setRepeat] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [invalid, setInvalid] = React.useState(!token);
  const [error, setError] = React.useState<string | null>(null);
  // Field messages show after the first submit, so typing is not interrupted by errors.
  const [tried, setTried] = React.useState(false);

  const shortError = password.length < PASSWORD_MIN_LENGTH ? "error.passwordShort" : undefined;
  const mismatchError = repeat !== password ? "login.reset.mismatch" : undefined;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setTried(true);
    if (busy || shortError || mismatchError) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ home: "/admin" | "/dock" }>("/auth/password/reset", { body: { token, password } });
      await qc.invalidateQueries({ queryKey: ["me"] });
      navigate(r.home, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) setInvalid(true);
      else setError(err instanceof ApiError ? err.message : "error.generic");
      setBusy(false);
    }
  }

  if (invalid) {
    return (
      <AuthShell title={t("login.reset.title")}>
        <ErrorText>{t("login.resetInvalid")}</ErrorText>
        <ButtonLink href="/login" variant="accent" className="h-12 text-16">
          {t("login.backToLogin")}
        </ButtonLink>
      </AuthShell>
    );
  }
  return (
    <AuthShell title={t("login.reset.title")}>
      <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
        <PasswordField label={t("login.reset.new")} value={password} onChange={setPassword} autoComplete="new-password" required tall error={tried ? shortError : undefined} />
        <PasswordField label={t("login.reset.repeat")} value={repeat} onChange={setRepeat} autoComplete="new-password" required tall error={tried ? mismatchError : undefined} />
        {error ? <ErrorText>{te(error)}</ErrorText> : null}
        <Button type="submit" variant="accent" className="h-12 text-16" disabled={busy}>
          {t("login.reset.submit")}
        </Button>
      </form>
    </AuthShell>
  );
}
